import './tokens.css';
import './style.css';
import { render } from 'preact';
import { clearSession, completeLogin, currentSession, takeReturnTo } from './auth.js';
import { getApiConfig, getMe, Unauthenticated } from './api.js';
import { Cache } from './cache.js';
import { t } from './i18n.js';
import './theme.js';
import { busy, emojiPalette, entities, folders, me, session, view } from './state.js';
import {
  act,
  applyView,
  loadFromApi,
  onCloseEditor,
  onOpenLink,
  onRefresh,
  pullChanges,
  refreshFolders,
} from './actions.js';
import { installHistory } from './navigation.js';
import { keepLink, parseLink, takeLink } from './link.js';
import { phase, Root } from './ui/app.js';

/**
 * L'amorçage : l'aiguillage d'authentification, puis le montage.
 *
 * Le rendu est **incrémental** depuis 4.0 : Preact diffe, la page n'est plus
 * reconstruite après chaque mutation. Le remplacement ne touche que la couche de
 * rendu — `api.ts`, `cache.ts`, `sync.ts`, `auth.ts` et `upload.ts` sont restés
 * tels quels, et les `data-testid` aussi. C'est la condition pour que les
 * parcours de bout en bout de l'étape 3 restent des témoins valables : un test
 * qu'on réécrit en même temps que le code ne prouve plus rien.
 *
 * **Aucun `innerHTML` nulle part.** Preact pose du texte par `textContent` ; un
 * nom de dossier, de fichier ou un alias viennent tous de quelqu'un.
 */

const host = document.getElementById('app')!;
// Le contenu d'attente de `index.html` n'est pas un enfant que Preact connaît :
// il faut le retirer, sinon il subsiste **sous** l'application montée.
host.replaceChildren();
render(<Root />, host);

/**
 * **Peindre d'abord, rattraper ensuite.**
 *
 * L'écran se dessine à partir du miroir, avant tout appel de contenu — un
 * rechargement montre donc l'espace immédiatement, y compris hors ligne. La
 * synchronisation part derrière et redessine quand elle a fini. L'ordre inverse,
 * qui est l'ordre naturel, coûte une seconde de page vide à chaque visite.
 */
async function showSignedIn(): Promise<void> {
  phase.value = { kind: 'busy', text: t('app.loadingAccount') };
  const profile = await getMe();

  const cache = await Cache.open(profile.uid);
  const cached = await cache?.read();

  me.value = profile;
  // La liste blanche d'emoji, **sans bloquer**. Un écran qui attendrait une
  // palette pour se peindre serait un écran qui attend une règle du jeu : elle
  // arrive, les boutons apparaissent. La proposer en dur ferait refuser en `400`
  // un emoji retiré de la liste, après le clic.
  void getApiConfig()
    .then((cfg) => (emojiPalette.value = cfg.emoji))
    .catch(() => undefined);
  session.cache = cache;
  session.cursor = cached?.cursor;
  entities.value = cached?.entities ?? [];
  folders.value = cached?.folders ?? [];
  phase.value = { kind: 'app' };

  await act(t('app.syncing'), async () => {
    // Sans miroir, `/changes` n'a nulle part où poser sa page ni où garder son
    // curseur : on retombe sur les listings, qui n'ont besoin d'aucun état local.
    if (cache) await pullChanges();
    else await Promise.all([refreshFolders(), loadFromApi()]);
  });
}

/**
 * **Le retour sur l'onglet vaut un appui sur *Actualiser*** *(2026-08-16)*.
 *
 * Le bouton seul ne suffit pas : il ne protège que celui qui pense à s'en servir,
 * et l'onglet laissé ouvert une journée est le cas normal d'une application web.
 * `visibilitychange` est le signal exact — il ne se déclenche pas au changement de
 * fenêtre, seulement quand l'onglet redevient visible.
 *
 * Deux gardes, et elles sont la raison pour laquelle ceci n'est pas un
 * *polling* déguisé : rien ne part si une action est déjà en vol (`busy`), et
 * rien ne part avant que l'application soit montée avec sa session. Le drainage
 * est incrémental — sur un miroir à jour, c'est **un appel qui rend une page
 * vide**.
 */
function refreshOnReturn(): void {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    if (phase.value.kind !== 'app' || busy.value) return;
    void onRefresh();
  });
}

/** Vrai quand on revient d'une ré-authentification demandée par la console d'admin. */
let retourAdmin = false;

async function main(): Promise<void> {
  // **Un lien reçu est mis de côté avant tout** *(L1)* — avant l'accueil, avant
  // la connexion : il doit traverser la redirection vers Cognito, et la barre
  // d'adresse revient à `/` pour qu'un rechargement ne le rejoue pas.
  const link = parseLink(location.pathname);
  if (link) keepLink(link);

  // **Le retour d'authentification a son propre traitement d'erreur**, et la
  // distinction n'est pas cosmétique : un échec ici veut dire « il n'y a pas de
  // session », donc l'accueil.
  if (location.pathname === '/callback') {
    phase.value = { kind: 'busy', text: t('app.signingIn') };
    try {
      await completeLogin(location.search);
    } catch (err) {
      clearSession();
      history.replaceState(null, '', '/');
      phase.value = { kind: 'landing', message: String(err) };
      return;
    }
    // L'URL porte encore le code d'autorisation : on la remplace avant tout
    // rendu, pour qu'un rechargement ne rejoue pas un code déjà consommé et que
    // le code ne reste pas dans l'historique.
    history.replaceState(null, '', '/');

    // **Revenir là d'où l'on est parti.** Une ré-authentification demandée depuis
    // la console d'administration (D21) doit y ramener : renvoyer sur l'accueil
    // ferait chercher à nouveau l'écran, et l'action qu'on venait de tenter
    // serait oubliée en route.
    //
    // L'action, elle, **n'est pas rejouée** : traverser une redirection avec une
    // écriture en attente reviendrait à créer un compte ou à couper un accès sans
    // que personne ait recliqué. On rouvre l'écran, l'autorisation est fraîche, et
    // le geste se refait en un clic.
    if (takeReturnTo() === 'admin') retourAdmin = true;
  }

  try {
    const current = await currentSession();
    if (!current) {
      phase.value = { kind: 'landing' };
      return;
    }
    await showSignedIn();
    if (retourAdmin) view.value = { kind: 'admin' };
    // **Le pont vers l'historique s'installe en dernier**, et l'ordre compte : il
    // remplace l'entrée courante par l'endroit où l'on vient d'atterrir. Installé
    // plus tôt, il aurait figé la vue d'avant le retour d'administration, et le
    // premier geste de retour aurait ramené sur un écran qu'on n'a jamais vu.
    installHistory({ applyView, closeEditor: () => void onCloseEditor() });
    // **Après le pont, et pas avant** : le lien empile le partage puis la
    // visionneuse, et c'est ce qui fait que fermer la photo ramène au partage, et
    // qu'un retour de plus ramène là où l'on a atterri.
    const pending = retourAdmin ? undefined : takeLink();
    if (pending) void onOpenLink(pending);
    refreshOnReturn();
  } catch (err) {
    if (err instanceof Unauthenticated) {
      clearSession();
      phase.value = { kind: 'landing', message: t('app.sessionExpired') };
      return;
    }
    phase.value = { kind: 'fatal', message: String(err) };
  }
}

void main();
